import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../shell/PageHeader';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import {
  listBackCaptureCombos,
  BackCaptureCombo,
} from '../../services/backCaptureService';
import { sprayService, SprayOperator, SprayMachine } from '../../services/sprayService';
import { DataTable, DataTableColumn, FilterBar } from '../patterns';
import { formatDate } from '../../dateFormat';
import './BackCaptureListPage.css';

type StatusFilter = 'all' | 'not_started' | 'in_progress';

const STATUS_LABEL_KEY: Record<BackCaptureCombo['status'], string> = {
  not_started: 'spray.backCapture.list.status.planned',
  in_progress:  'spray.backCapture.list.status.inProgress',
  // completed / incomplete / cancelled are filtered out server-side on the list
  // endpoint; these branches are unreachable in the current list scope but are
  // kept to satisfy the exhaustive Record<BackCaptureCombo['status'], string> type.
  completed:    'spray.backCapture.list.status.completed',
  incomplete:   'spray.backCapture.list.status.completed',
  cancelled:    'spray.backCapture.list.status.completed',
};

const BackCaptureListPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [combos, setCombos] = useState<BackCaptureCombo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  const [operatorsById, setOperatorsById] = useState<Map<string, SprayOperator>>(new Map());
  const [machinesById, setMachinesById] = useState<Map<string, SprayMachine>>(new Map());

  useBreadcrumb([
    { label: 'Citrus Synch', to: '/' },
    { label: t('spray.title'), to: '/protection' },
    { label: t('spray.backCapture.list.title') },
  ]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      listBackCaptureCombos(),
      sprayService.getOperators(),
      sprayService.getMachines(),
    ])
      .then(([rows, operators, machines]) => {
        if (cancelled) return;
        setCombos(rows);
        setOperatorsById(new Map(operators.map(o => [o._id, o])));
        setMachinesById(new Map(machines.map(m => [m._id, m])));
      })
      .catch(() => { if (!cancelled) setError(t('spray.backCapture.list.error')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusCounts = useMemo(() => {
    const counts = { all: combos.length, not_started: 0, in_progress: 0 };
    for (const c of combos) {
      if (c.status === 'not_started') counts.not_started += 1;
      else if (c.status === 'in_progress') counts.in_progress += 1;
    }
    return counts;
  }, [combos]);

  const filtered = useMemo(() => {
    return combos.filter(c => {
      if (statusFilter !== 'all' && c.status !== statusFilter) return false;
      if (search.trim()) {
        const needle = search.toLowerCase();
        const operator = operatorsById.get(c.operatorId);
        const machine  = machinesById.get(c.machineId);
        const hay = [
          c.operatorId, c.machineId,
          operator?.name ?? '',
          machine?.name ?? '',
          ...(c.parent?.orchardIds ?? []),
          c.parent?.name ?? '',
        ].join(' ').toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [combos, statusFilter, search, operatorsById, machinesById]);

  const columns: DataTableColumn<BackCaptureCombo>[] = [
    {
      key: 'date',
      label: t('spray.backCapture.list.col.date'),
      render: (c) => (
        <span data-testid={`back-capture-row-${c._id}`}>
          {c.parent?.date ? formatDate(c.parent.date as string, { timeZone: 'UTC' }) : '—'}
        </span>
      ),
    },
    {
      key: 'session',
      label: t('spray.backCapture.list.col.session'),
      render: (c) => {
        const { seq, name } = c.parent ?? {};
        if (seq === undefined) return '—';
        return name ? `SES-${seq} · ${name}` : `SES-${seq}`;
      },
    },
    {
      key: 'operator',
      label: t('spray.backCapture.list.col.operator'),
      render: (c) => operatorsById.get(c.operatorId)?.name ?? c.operatorId,
    },
    {
      key: 'machine',
      label: t('spray.backCapture.list.col.machine'),
      render: (c) => machinesById.get(c.machineId)?.name ?? c.machineId,
    },
    {
      key: 'status',
      label: t('spray.backCapture.list.col.status'),
      render: (c) => t(STATUS_LABEL_KEY[c.status]),
    },
    {
      key: 'lastUpdated',
      label: t('spray.backCapture.list.col.lastUpdated'),
      render: (c) => c.startedAt ? formatDate(c.startedAt, { timeZone: 'UTC' }) : '—',
    },
  ];

  return (
    <div className="back-capture-list">
      <PageHeader
        title={t('spray.backCapture.list.title')}
        meta={t('spray.backCapture.list.subtitle')}
      />

      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder={t('spray.backCapture.list.search.placeholder')}
        chips={[
          {
            id: 'all',
            label: t('spray.backCapture.list.filter.all'),
            count: statusCounts.all,
            active: statusFilter === 'all',
          },
          {
            id: 'not_started',
            label: t('spray.backCapture.list.status.planned'),
            count: statusCounts.not_started,
            active: statusFilter === 'not_started',
          },
          {
            id: 'in_progress',
            label: t('spray.backCapture.list.status.inProgress'),
            count: statusCounts.in_progress,
            active: statusFilter === 'in_progress',
          },
        ]}
        onChipClick={(id) => setStatusFilter(id as StatusFilter)}
      />

      {loading ? (
        <div className="back-capture-list__empty" role="status">
          {t('spray.backCapture.list.loading')}
        </div>
      ) : error ? (
        <div className="back-capture-list__error" role="alert">
          {error}
        </div>
      ) : (
        <DataTable<BackCaptureCombo>
          columns={columns}
          rows={filtered}
          rowKey={(c) => c._id}
          onRowClick={(c) => navigate(`/protection/back-capture/${c._id}`)}
          emptyState={
            <div className="back-capture-list__empty" role="status">
              {t('spray.backCapture.list.empty')}
            </div>
          }
        />
      )}
    </div>
  );
};

export default BackCaptureListPage;
